import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import { Button } from 'react-bootstrap';
import PastRoleForm from './PastRoleForm';
import { orderByStartDate } from '../../../arrayUtils';
import './PastRolesQuestion.scss';

const months = [
  { label: 'January', value: '0' },
  { label: 'February', value: '1' },
  { label: 'March', value: '2' },
  { label: 'April', value: '3' },
  { label: 'May', value: '4' },
  { label: 'June', value: '5' },
  { label: 'July', value: '6' },
  { label: 'August', value: '7' },
  { label: 'September', value: '8' },
  { label: 'October', value: '9' },
  { label: 'November', value: '10' },
  { label: 'December', value: '11' },
];

const initializedPastRole = () => (
  {
    title: '',
    role: '',
    companyName: '',
    description: '',
    startDateMonth: null,
    startDateYear: null,
    endDateMonth: null,
    endDateYear: null,
    current: false,
  }
);

const pastRoleIndex = (pastRoles, objectId) => (objectId
  ? pastRoles.findIndex((pastRole) => pastRole.id === objectId)
  : pastRoles.findIndex((pastRole) => !Object.hasOwn(pastRole, 'id')));

const editablePastRoles = (pastRoles) => orderByStartDate(pastRoles).map((pastRole) => {
  const pr = {
    id: pastRole.id,
    title: pastRole.title,
    role: pastRole.role,
    companyName: pastRole.company_name,
    description: pastRole.description,
    current: pastRole.current,
    is_suggested: pastRole.is_suggested,
  };
  if (pastRole.start_date) {
    const startDateArray = pastRole.start_date.split('-').map((x) => Number(x));
    // Subtracted 1 to get the correct month
    const startDate = new Date(startDateArray[0], startDateArray[1] - 1, startDateArray[2]);
    pr.startDateMonth = months[startDate.getMonth()];
    const startYear = startDate.toLocaleString('default', { year: 'numeric' });
    pr.startDateYear = { label: startYear, value: startYear };
  }
  if (pastRole.end_date) {
    const endDateArray = pastRole.end_date.split('-').map((x) => Number(x));
    const endDate = new Date(endDateArray[0], endDateArray[1] - 1, endDateArray[2]);
    pr.endDateMonth = months[endDate.getMonth()];
    const endYear = endDate.toLocaleString('default', { year: 'numeric' });
    pr.endDateYear = { label: endYear, value: endYear };
  }
  return pr;
});

const submittablePastRoles = (newPastRoles) => (
  newPastRoles.map((pastRole) => {
    let submittableStartDate = null;
    let submittableEndDate = null;
    if (pastRole.startDateMonth) {
      submittableStartDate = new Date(
        Date.UTC(pastRole.startDateYear.label, pastRole.startDateMonth.value),
      ).toISOString();
    } else if (pastRole.startDateYear) {
      submittableStartDate = new Date(`${pastRole.startDateYear.label}`);
    }
    if (pastRole.endDateMonth) {
      submittableEndDate = new Date(
        Date.UTC(pastRole.endDateYear.label, pastRole.endDateMonth.value),
      ).toISOString();
    } else if (pastRole.endDateYear) {
      submittableEndDate = new Date(`${pastRole.endDateYear.label}`);
    }
    const pr = {
      id: pastRole?.id,
      title: pastRole.title,
      role: pastRole.role,
      company_name: pastRole.companyName,
      description: pastRole.description,
      start_date: submittableStartDate,
      current: pastRole.current,
      is_suggested: pastRole.is_suggested,
      is_edited: pastRole.is_edited || false,
      end_date: submittableEndDate,
    };
    return pr;
  })
);

const setNewPastRoles = (objectId, onboardingQuestionnaire, pastRoles, onProposalPage) => {
  let newPastRoles = (objectId || onboardingQuestionnaire)
    ? editablePastRoles(pastRoles) : [...editablePastRoles(pastRoles), initializedPastRole()];

  if (onProposalPage) {
    newPastRoles = [];
  }

  return newPastRoles;
};

const validatePastRole = (pastRole) => {
  // If it is a delete action return [] or Skip Validation for Saved Roles
  if (!pastRole || pastRole.id) return [];

  const error = [];

  if (!pastRole.title) error.push('title');
  if (!pastRole.companyName) error.push('company name');
  if (pastRole.startDateYear == null) error.push('start date');

  if (!pastRole.current) {
    if (pastRole.endDateYear == null) {
      error.push('end date');
    } else if (!error.includes('start date')) {
      const safeStartMonth = pastRole.startDateMonth ? pastRole.startDateMonth.value : null;
      const safeEndMonth = pastRole.endDateMonth ? pastRole.endDateMonth.value : null;
      const startDate = new Date(pastRole.startDateYear.value, safeStartMonth);
      const endDate = new Date(pastRole.endDateYear.value, safeEndMonth);

      if (startDate > endDate) {
        error.push('end date', 'The end date must be after the start date.');
      }
    }
  }

  return error;
};

class PastRolesQuestion extends Component {
  constructor(props) {
    super(props);
    const {
      objectId, onboardingQuestionnaire, newPastRoles, onProposalPage,
    } = props;
    this.state = {
      errors: [],
      newPastRoles: setNewPastRoles(objectId, onboardingQuestionnaire, newPastRoles, onProposalPage),
    };
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleDeletePastRole = this.handleDeletePastRole.bind(this);
    this.handleSubmit = debounce(this.handleSubmit.bind(this), 500);
  }

  componentDidMount() {
    // Initially save the suggested past roles for onboarding because it is autofilled
    // and edits triggers saves
    if (this.props.saveOnChange && this.props.newPastRoles) {
      const { handleChange, questionKey } = this.props;
      const { newPastRoles } = this.state;
      handleChange(submittablePastRoles(newPastRoles), questionKey, null, false);
    }
  }

  handleInputChange(value, key, index) {
    const { newPastRoles } = this.state;
    const { saveOnChange } = this.props;

    newPastRoles[index][key] = value;
    newPastRoles[index].is_edited = true;
    this.setState({ newPastRoles });
    if (saveOnChange) {
      this.handleSubmit();
    }
  }

  handleDateChange(value, key, index) {
    const { newPastRoles } = this.state;
    newPastRoles[index][key] = value;
    newPastRoles[index].is_edited = true;
    this.setState({ newPastRoles });
    const { saveOnChange } = this.props;
    if (saveOnChange) {
      this.handleSubmit();
    }
  }

  handleDeletePastRole(index) {
    const { newPastRoles, errors } = this.state;
    const { onProposalPage } = this.props;
    const initialLength = newPastRoles.length;
    newPastRoles.splice(index, 1);
    const newLength = newPastRoles.length;
    errors.splice(index, 1);
    this.setState({ newPastRoles, errors });
    const { saveOnChange } = this.props;
    if (saveOnChange || initialLength !== newLength) {
      this.handleSubmit(!onProposalPage);
    }
  }

  handleValidation() {
    const { objectId } = this.props;
    const { newPastRoles } = this.state;
    const modifiedIndex = pastRoleIndex(newPastRoles, objectId);
    const pastRole = newPastRoles[modifiedIndex];
    if (objectId) {
      return [validatePastRole(pastRole)];
    }
    const errors = newPastRoles.map((pr) => {
      const error = validatePastRole(pr);
      return error;
    });

    return errors;
  }

  handleAddPastRole() {
    const { newPastRoles } = this.state;
    newPastRoles.push(initializedPastRole());
    this.setState({ newPastRoles });
  }

  handleSubmit(shouldModalClose = true) {
    const { newPastRoles } = this.state;
    const {
      onboardingQuestionnaire,
      profileQuestionnaire,
      handleChange,
      questionKey,
      closeModal,
      isReadingMore,
      handlePastRolesAfterUpdate,
      onProposalPage,
    } = this.props;

    const errors = this.handleValidation();
    if (errors.every((error) => error.length === 0)) {
      handleChange(submittablePastRoles(newPastRoles), questionKey, null, false)
        .then((res) => {
          if (res) {
            handlePastRolesAfterUpdate(res.data);
          }
        });

      // On onboarding, we do not re-insert an empty one
      const updatedState = onboardingQuestionnaire || onProposalPage
        ? { newPastRoles, errors: [] }
        : { newPastRoles: [...newPastRoles.filter((pr) => pr.id), initializedPastRole()] };

      this.setState(updatedState, () => {
        if (shouldModalClose) {
          closeModal();
        }
        if (profileQuestionnaire) {
          const currentPath = window.location.href;
          const noParamsPath = currentPath.substring(0, currentPath.indexOf('?'));
          window.history.pushState(
            null,
            'Clora Consultant Profile',
            `${noParamsPath}?is_reading_more=${isReadingMore}`,
          );
        }
      });
    } else {
      this.setState({ errors });
    }
  }

  renderNewPastRoles() {
    const {
      showBorder, onboardingQuestionnaire, onProposalPage, objectId,
    } = this.props;
    const { newPastRoles, errors } = this.state;
    const modifiedIndex = pastRoleIndex(newPastRoles, objectId);
    const pastRole = newPastRoles[modifiedIndex];

    // On the Consultant Onboarding flow, we render one form per existing Past Role
    if (onboardingQuestionnaire || onProposalPage) {
      return (
        <form className={`new-past-roles ${showBorder && 'border'}`}>
          {newPastRoles.map((pr, index) => (
            <div className="new-past-role" key={pr?.id}>
              <input type="hidden" name="is_suggested" value={pr.is_suggested} />
              <PastRoleForm
                classNamePrefix="profile-add-past-role"
                index={index}
                pastRole={pr}
                error={errors[index] || []}
                handleInputChange={this.handleInputChange}
                handleDateChange={this.handleDateChange}
                handleDeletePastRole={this.handleDeletePastRole}
                onboardingQuestionnaire={onboardingQuestionnaire}
                onProposalPage={onProposalPage}
              />
            </div>
          ))}
        </form>
      );
    } if (pastRole) {
      return (
        <form className={`new-past-roles ${showBorder && 'border'}`}>
          <div className="new-past-role" key={pastRole?.id}>
            <input type="hidden" name="is_suggested" value={pastRole.is_suggested} />
            <PastRoleForm
              classNamePrefix="profile-add-past-role"
              index={modifiedIndex}
              pastRole={pastRole}
              error={errors[modifiedIndex]}
              handleInputChange={this.handleInputChange}
              handleDateChange={this.handleDateChange}
              handleDeletePastRole={this.handleDeletePastRole}
              onboardingQuestionnaire={onboardingQuestionnaire}
              onProposalPage={onProposalPage}
            />
          </div>
        </form>
      );
    }

    return null;
  }

  render() {
    const {
      disableSubmit, saveOnChange, onboardingQuestionnaire, onProposalPage,
    } = this.props;
    const { newPastRoles } = this.state;
    return (
      <Fragment>
        {newPastRoles.length > 0 && this.renderNewPastRoles()}
        <div className="footer">
          {(onboardingQuestionnaire || onProposalPage)
            && (
            <a
              role="link"
              tabIndex={0}
              id="add-past-role"
              onClick={() => this.handleAddPastRole()}
            >
              <i className="fa fa-plus mr-5" />
              {`${newPastRoles.length === 0 ? 'Add Role' : 'Add another role'}`}
            </a>
            )}
          { newPastRoles.length > 0 && !saveOnChange
            && (
            <div className="buttons-bar text-center">
              <Button
                variant="primary"
                id="update-proposal-past-roles"
                className="save"
                onClick={this.handleSubmit}
                disabled={disableSubmit && newPastRoles.length === 0}
              >
                Save
              </Button>
            </div>
            )}
        </div>
      </Fragment>
    );
  }
}

PastRolesQuestion.propTypes = {
  newPastRoles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  handleChange: PropTypes.func.isRequired,
  questionKey: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
  disableSubmit: PropTypes.bool,
  showBorder: PropTypes.bool,
  saveOnChange: PropTypes.bool,
  onboardingQuestionnaire: PropTypes.bool,
  profileQuestionnaire: PropTypes.bool,
  onProposalPage: PropTypes.bool,
  objectId: PropTypes.number,
  isReadingMore: PropTypes.bool,
  handlePastRolesAfterUpdate: PropTypes.func,
};

PastRolesQuestion.defaultProps = {
  disableSubmit: true,
  showBorder: false,
  saveOnChange: false,
  onboardingQuestionnaire: false,
  profileQuestionnaire: false,
  onProposalPage: false,
  objectId: null,
  isReadingMore: false,
  handlePastRolesAfterUpdate: () => {},
};

export default PastRolesQuestion;
